import React, { useState, useRef, useEffect, cloneElement } from 'react';
import { Check } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Portal } from '../../utils/Portal';
import { useFloatingPosition } from '../../utils/useFloatingPosition';

/**
 * DropdownMenu — menu contextuel rendu via Portal pour échapper aux
 * overflow:hidden des conteneurs parents (ex. wrapper DataTable).
 *
 * Props :
 *   - trigger : élément déclencheur (cloneElement → onClick)
 *   - side    : 'bottom' | 'top' | 'left' | 'right'
 *   - align   : 'start' | 'end' | 'center'
 */
export function DropdownMenu({ trigger, side = 'bottom', align = 'start', children, className, triggerWrapperClassName = 'inline-block' }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const floatingRef = useRef(null);

  const { style } = useFloatingPosition({ triggerRef, floatingRef, open, side, align, offset: 4 });

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (floatingRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onEsc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const triggerEl = cloneElement(trigger, {
    onClick: (e) => { setOpen((o) => !o); trigger.props.onClick?.(e); },
    'aria-expanded': open,
    'aria-haspopup': 'menu',
  });

  return (
    <>
      <span ref={triggerRef} className={triggerWrapperClassName}>
        {triggerEl}
      </span>
      {open && (
        <Portal>
          <div
            ref={floatingRef}
            role="menu"
            data-ds-theme="default"
            style={style}
            className={cn(
              'z-overlay min-w-[200px] p-1 bg-surface border border-border rounded-md shadow-md animate-scale-in origin-top-left',
              className,
            )}
          >
            {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
          </div>
        </Portal>
      )}
    </>
  );
}

function Item({ icon, shortcut, danger, disabled, onSelect, children, className }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onSelect}
      disabled={disabled}
      className={cn(
        'w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded text-sm text-left transition-colors',
        'hover:bg-bg-2',
        danger && 'text-danger hover:bg-danger-bg',
        disabled && 'opacity-50 pointer-events-none',
        className,
      )}
    >
      {icon && <span className="text-mute [&_svg]:w-4 [&_svg]:h-4">{icon}</span>}
      <span className="flex-1 flex items-center gap-2 min-w-0">{children}</span>
      {shortcut && <span className="text-xs font-mono text-mute">{shortcut}</span>}
    </button>
  );
}

function Separator({ className }) {
  return <hr className={cn('my-1 border-0 h-px bg-border', className)} />;
}

function Label({ children, className }) {
  return <div className={cn('px-2.5 pt-1.5 pb-1 text-[10.5px] uppercase tracking-wider text-mute font-mono', className)}>{children}</div>;
}

function CheckboxItem({ checked, onCheckedChange, children, className }) {
  return (
    <button type="button" role="menuitemcheckbox" aria-checked={checked} onClick={() => onCheckedChange?.(!checked)} className={cn('w-full flex items-center gap-2.5 px-2.5 py-1.5 rounded text-sm hover:bg-bg-2 transition-colors', className)}>
      <span className="w-4 h-4 inline-flex items-center justify-center">{checked && <Check className="w-3.5 h-3.5 text-text" strokeWidth={2.5} />}</span>
      <span className="flex-1 text-left">{children}</span>
    </button>
  );
}

DropdownMenu.Item = Item;
DropdownMenu.Separator = Separator;
DropdownMenu.Label = Label;
DropdownMenu.CheckboxItem = CheckboxItem;

export default DropdownMenu;
